import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { inr, fmtDate, DOC_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList } from "@/components/Pagination";
import { PortButtons } from "@/components/DataPorting";
import RecurringInvoices from "@/components/RecurringInvoices";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FILTERS = ["ALL", "DRAFT", "UNPAID", "PARTIALLY_PAID", "OVERDUE", "PAID", "CANCELLED"];

export default function Documents({ docType = "SALES_INVOICE,BILL_OF_SUPPLY", title = "Invoices" }) {
  const [q, setQ] = useState("");
  // Recurring lives here rather than in the nav: a schedule is an invoice that
  // has not happened yet, not a different kind of thing.
  const [tab, setTab] = useState("list");
  const [filter, setFilter] = useState("ALL");
  const [range, setRange] = useState({ from_date: "", to_date: "" });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isCN = docType === "CREDIT_NOTE";
  const showTabs = !isCN;

  useEffect(() => {
    const wanted = params.get("tab");
    if (wanted === "recurring") setTab("recurring");
  }, [params]);

  // Filtering happens server-side now: filtering a single page client-side
  // would silently hide matches sitting on other pages.
  const query = useMemo(() => ({
    type: docType,
    q: q || undefined,
    status: filter === "ALL" ? undefined : filter,
    from_date: range.from_date || undefined,
    to_date: range.to_date || undefined,
  }), [docType, q, filter, range]);

  const { rows, total, page, setPage, size, setSize, loading } =
    usePagedList("/documents", query);

  const totals = useMemo(
    () => ({
      value: rows.reduce((s, d) => s + (d.totals?.grand_total || 0), 0),
      due: rows.reduce((s, d) => s + (d.status === "ISSUED" ? d.balance_due || 0 : 0), 0),
    }),
    [rows]
  );

  return (
    <div className="space-y-6" data-testid={isCN ? "credit-notes-page" : "invoices-page"}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {rows.length} document{rows.length === 1 ? "" : "s"} · value{" "}
            <span className="font-mono">{inr(totals.value)}</span> · outstanding{" "}
            <span className="font-mono text-rose-700">{inr(totals.due)}</span>
          </p>
        </div>
        {tab === "list" && (
          <div className="flex flex-wrap items-center gap-2">
            <PortButtons datasetKey="invoices" />
            <PortButtons datasetKey="invoice_lines" label="lines" />
            {!isCN && (
              <Button data-testid="new-invoice-btn" onClick={() => navigate("/invoices/new")} className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
                <Plus size={16} /> New Invoice
              </Button>
            )}
          </div>
        )}
      </div>

      {showTabs && (
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {[["list", "Invoices"], ["recurring", "Recurring"]].map(([key, label]) => (
            <button key={key} data-testid={`invoices-tab-${key}`} onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                tab === key ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === "recurring" && <RecurringInvoices />}

      {tab === "list" && (<>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="documents-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search number or party"
            className="h-9 w-full pl-8 sm:w-64"
          />
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">From</Label>
            <Input type="date" className="h-9 w-36" data-testid="docs-from" value={range.from_date}
              onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">To</Label>
            <Input type="date" className="h-9 w-36" data-testid="docs-to" value={range.to_date}
              onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
          </div>
          {(range.from_date || range.to_date) && (
            <button className="h-9 px-2 text-[11px] text-slate-500 hover:text-slate-800"
              data-testid="docs-clear-dates"
              onClick={() => setRange({ from_date: "", to_date: "" })}>Clear</button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`inline-flex min-h-[36px] items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-150 sm:min-h-0 ${
                filter === f ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              {f.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="documents-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Number</th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Party</th>
              <th className="px-3 py-2.5 text-left font-semibold">GSTIN</th>
              <th className="px-3 py-2.5 text-right font-semibold">Taxable</th>
              <th className="px-3 py-2.5 text-right font-semibold">Tax</th>
              <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
              <th className="px-4 py-2.5 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((d) => (
              <tr
                key={d.id}
                data-testid={`document-row-${d.id}`}
                onClick={() => navigate(`/invoices/${d.id}`)}
                className="cursor-pointer hover:bg-blue-50/40 transition-colors duration-150"
              >
                <td className="px-4 py-2.5">
                  <div className="font-mono text-xs font-semibold">{d.document_number || "DRAFT"}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{DOC_LABEL[d.document_type]}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(d.document_date)}</td>
                <td className="px-3 py-2.5 max-w-[200px] truncate">{d.party_name}</td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{d.party_gstin || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs">{inr(d.totals?.taxable_value)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs">{inr(d.totals?.total_tax)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold">{inr(d.totals?.grand_total)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-rose-700">
                  {d.status === "ISSUED" && d.balance_due > 0.01 ? inr(d.balance_due) : "—"}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <StatusBadge status={d.status === "ISSUED" ? d.payment_status : d.status} />
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={9} className="px-4 py-14 text-center text-sm text-slate-500">
                  {loading ? "Loading…" : `No ${title.toLowerCase()} found`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty={loading ? "Loading…" : `No ${title.toLowerCase()} found`} testid="documents-cards">
        {rows.map((d) => (
          <DataCard
            key={d.id}
            testid={`document-card-${d.id}`}
            onClick={() => navigate(`/invoices/${d.id}`)}
            title={d.document_number || "DRAFT"}
            subtitle={`${d.party_name} · ${DOC_LABEL[d.document_type] || ""}`}
            right={
              <>
                <div className="font-mono text-sm font-bold tabular-nums">{inr(d.totals?.grand_total)}</div>
                <div className="mt-1">
                  <StatusBadge status={d.status === "ISSUED" ? d.payment_status : d.status} />
                </div>
              </>
            }
            rows={[
              ["Date", fmtDate(d.document_date)],
              ["Taxable", inr(d.totals?.taxable_value)],
              Number(d.totals?.total_tax) ? ["Tax", inr(d.totals?.total_tax)] : null,
              d.status === "ISSUED" && d.balance_due > 0.01
                ? ["Balance", <span className="text-rose-700">{inr(d.balance_due)}</span>]
                : null,
            ]}
          />
        ))}
      </CardList>

      <Pagination page={page} setPage={setPage} size={size} setSize={setSize}
        total={total} loading={loading} testid="documents-pagination" />
      </>)}
    </div>
  );
}
